import { useEffect, useRef, memo } from "react";

const NODE_COUNT = 18;
const CONNECTION_DISTANCE = 180;
const COLORS = ["#7C4DFF", "#27B8FF", "#A78BFA", "#60A5FA"];

const createNode = (w, h) => ({
  x: 50 + Math.random() * (w - 100),
  y: 50 + Math.random() * (h - 100),
  vx: (Math.random() - 0.5) * 0.4,
  vy: (Math.random() - 0.5) * 0.4,
  size: 2.5 + Math.random() * 3,
  color: COLORS[Math.floor(Math.random() * COLORS.length)],
  pulsePhase: Math.random() * Math.PI * 2,
  pulseSpeed: 0.02 + Math.random() * 0.03,
});

const NeuralNetwork = () => {
  const canvasRef = useRef(null);
  const nodesRef = useRef([]);
  const animFrameRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.scale(dpr, dpr);
    };

    resize();

    const w = window.innerWidth;
    const h = window.innerHeight;
    nodesRef.current = Array.from({ length: NODE_COUNT }, () =>
      createNode(w, h)
    );

    let time = 0;

    const animate = () => {
      time += 1;
      const width = window.innerWidth;
      const height = window.innerHeight;
      ctx.clearRect(0, 0, width, height);

      const nodes = nodesRef.current;

      // Update positions
      nodes.forEach((node) => {
        node.x += node.vx;
        node.y += node.vy;

        // Bounce off edges with padding
        if (node.x < 30 || node.x > width - 30) node.vx *= -1;
        if (node.y < 30 || node.y > height - 30) node.vy *= -1;

        // Keep in bounds
        node.x = Math.max(20, Math.min(width - 20, node.x));
        node.y = Math.max(20, Math.min(height - 20, node.y));
      });

      // Draw connections
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i];
          const b = nodes[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < CONNECTION_DISTANCE) {
            const opacity = (1 - dist / CONNECTION_DISTANCE) * 0.25;
            const pulse = Math.sin(time * 0.02 + i + j) * 0.5 + 0.5;
            const finalOpacity = opacity * (0.5 + 0.5 * pulse);

            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.strokeStyle = `rgba(124, 77, 255, ${finalOpacity})`;
            ctx.lineWidth = 0.8;
            ctx.stroke();
          }
        }
      }

      // Draw nodes
      nodes.forEach((node) => {
        const pulse = Math.sin(time * node.pulseSpeed + node.pulsePhase);
        const currentSize = node.size * (0.85 + 0.15 * pulse);
        const glowIntensity = 0.5 + 0.5 * pulse;

        // Outer glow
        const grad = ctx.createRadialGradient(
          node.x, node.y, 0,
          node.x, node.y, currentSize * 5
        );
        grad.addColorStop(0, node.color + Math.round(glowIntensity * 40).toString(16).padStart(2, "0"));
        grad.addColorStop(1, "transparent");
        ctx.beginPath();
        ctx.arc(node.x, node.y, currentSize * 5, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();

        // Core dot
        ctx.beginPath();
        ctx.arc(node.x, node.y, currentSize, 0, Math.PI * 2);
        ctx.fillStyle = node.color;
        ctx.fill();
      });

      animFrameRef.current = requestAnimationFrame(animate);
    };

    animate();
    window.addEventListener("resize", resize);

    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none fixed inset-0 z-[2]"
      style={{ opacity: 0.6, willChange: "transform" }}
    />
  );
};

export default memo(NeuralNetwork);
